import { Row, Col, Button, Spinner } from 'react-bootstrap'
import { Formik, Field, Form } from 'formik'
import * as Yup from 'yup'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import dayjs from 'dayjs'
import axios from 'axios'
import { AuthContext } from '../AuthContext'
import { useContext, useState } from 'react'

const MetabolicPlanFormConfirm = ({
  warning,
  setWarning,
  setQrcode,
  setCaseModalShow,
  currentPatient,
  formData,
  setFormConfirmShow,
  lastTrackFormData,
  currentTrackIndex,
  isTrackDataLoaded,
}) => {
  const initialValues = {
    project_id: 'P0001',
    clinic_id: '',
    record_time: dayjs().format('YYYY-MM-DD'),
    next_appointment_time: '',
    track_type: '',
    id_number: isTrackDataLoaded ? lastTrackFormData.id_number : '',
    phone: isTrackDataLoaded ? lastTrackFormData.phone : '',
    family_name: '', // 這個留空
  }
  // console.log('confirm formData', formData)
  const { name, gender, birthday, track_form } = formData
  const { info, chronic_risk, indicator, guidelines } = track_form
  const age = dayjs().diff(birthday, 'year')
  const { baseUrl, sendDiscordNotification, currentUser, extendCookie } =
    useContext(AuthContext)
  const createPatient = '/api/crm/project/patient/createPatient'
  const addTrack = '/api/crm/project/track/addTrack'
  const [isCardLoading, setIsCardLoading] = useState(false)

  // 戒檳、運動轉換小工具
  function transformChoicesToArray(choices) {
    switch (JSON.stringify(choices)) {
      case JSON.stringify(['0', '1']):
        return [1, 1]
      case JSON.stringify(['0']):
        return [1, 0]
      case JSON.stringify(['1']):
        return [0, 1]
      default:
        return [0, 0]
    }
  }

  const allFoodChoices = [
    'friedFood',
    'sweets',
    'salt',
    'sugaryDrinks',
    'others',
  ]
  const reduceFoodTypeArr = allFoodChoices.map((choice) =>
    guidelines.food.reduce_food_type.includes(choice) ? 1 : 0
  )

  const allOtherTypeChoices = ['blood_sugar', 'HbA1c', 'tg', 'hdl', 'ldl']
  const otherTypeArr = allOtherTypeChoices.map((choice) =>
    formData.track_form.guidelines.other.other_type.includes(choice) ? 1 : 0
  )

  // error classname
  function getClassName(errors, touched, fieldName) {
    if (fieldName.includes('.')) {
      const fieldParts = fieldName.split('.')
      let currentError = errors
      let currentTouched = touched

      for (const part of fieldParts) {
        if (currentError && currentTouched) {
          currentError = currentError[part]
          currentTouched = currentTouched[part]
        } else {
          break
        }
      }
      return currentError && currentTouched
        ? 'border border-danger border-2'
        : ''
    } else {
      return errors[fieldName] && touched[fieldName]
        ? 'border border-danger border-2'
        : ''
    }
  }

  // error message
  function renderErrorMessage(errors, touched, fieldName) {
    if (fieldName.includes('.')) {
      const fieldParts = fieldName.split('.')
      let currentError = errors
      let currentTouched = touched

      for (const part of fieldParts) {
        if (currentError && currentTouched) {
          currentError = currentError[part]
          currentTouched = currentTouched[part]
        } else {
          break
        }
      }
      return currentError && currentTouched ? (
        <div className="error-message">{currentError}</div>
      ) : null
    } else {
      return errors[fieldName] && touched[fieldName] ? (
        <div className="error-message">{errors[fieldName]}</div>
      ) : null
    }
  }

  // 戒菸轉換小工具
  function transformRadioValueToArray(value) {
    switch (value) {
      case '0':
        return [1, 0]
      case '1':
        return [0, 1]
      default:
        return [0, 0]
    }
  }

  // 物件轉數字小工具
  function convertAllStringsToNumbers(obj) {
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === 'string') {
        const convertedNumber = Number(obj[key])
        obj[key] = isNaN(convertedNumber) ? obj[key] : convertedNumber
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        obj[key] = convertAllStringsToNumbers(obj[key])
      }
    })
    return obj
  }

  // Form submit handler
  const handleSubmit = (values) => {
    console.log('values', values)
    const requestBody = {
      ...values,
      track_type: Number(values.track_type),
      clinic_id: currentUser.clinic_id,
      ...formData,
      gender: lastTrackFormData.gender || Number(formData.gender),
      track_form: {
        ...track_form,
        info: {
          ...convertAllStringsToNumbers(info),
        },
        chronic_risk: {
          ...convertAllStringsToNumbers(chronic_risk),
        },
        indicator: {
          ...convertAllStringsToNumbers(indicator),
          bmi: Number(
            (info.weight / Math.pow(info.height / 100, 2)).toFixed(2)
          ),
        },

        guidelines: {
          ...guidelines,
          quit_smoking: {
            ...guidelines.quit_smoking,
            check: Number(guidelines.quit_smoking.check),
            quit_smoking_type: transformRadioValueToArray(
              guidelines.quit_smoking.quit_smoking_type
            ),
            service_type: transformRadioValueToArray(
              guidelines.quit_smoking.service_type
            ),
          },
          quit_betel_nut: {
            ...guidelines.quit_betel_nut,
            check: Number(guidelines.quit_betel_nut.check),
            quit_betel_nut_type: transformChoicesToArray(
              guidelines.quit_betel_nut.quit_betel_nut_type
            ),
          },
          food: {
            ...convertAllStringsToNumbers(guidelines.food),
            reduce_food_type: reduceFoodTypeArr,
          },
          exercise: {
            ...guidelines.exercise,
            check: Number(guidelines.exercise.check),
            exercise_type: transformChoicesToArray(
              guidelines.exercise.exercise_type
            ),
          },
          other: {
            ...guidelines.other,
            other_type: otherTypeArr,
          },
        },
      },
    }
    console.log('requestBody', requestBody)
    const {
      name,
      birthday,
      family_name,
      gender,
      id_number,
      phone,
      ...restOfRequestBody
    } = requestBody

    const trackRequestBody = {
      ...restOfRequestBody,
      type: currentTrackIndex === 5 ? 1 : 0,
      patient_id: currentPatient ? currentPatient.patient_id : '',
    }
    console.log('trackRequestBody', trackRequestBody)
    setIsCardLoading(true)
    setWarning({
      ...warning,
      show: true,
      msg: '請再次確保表單內容資料正確，送出後無法修改！',
      confirmMsg: '確認送出',
      onConfirm: () => {
        if (currentUser && currentUser.session) {
          let loadingTimer

          loadingTimer = setTimeout(() => {
            // setIsCardLoading(true)
            setWarning({ ...warning, isLoading: true })
          }, 500)

          if (currentPatient && lastTrackFormData) {
            axios
              .post(baseUrl + addTrack, trackRequestBody, {
                headers: {
                  'Content-Type': 'application/json; charset=UTF-8',
                  Accept: '*/*',
                  session: currentUser.session,
                },
              })
              .then((res) => {
                if (res.data.message === '新增追蹤成功') {
                  console.log('addTrack', res.data)
                  // setbloodPressureList(res.data.data)
                  setQrcode({ show: true, msg: '資料建立成功！' })
                  setCaseModalShow(false)
                  extendCookie()
                }
              })
              .catch((e) => {
                console.log('addTrack error', e)
                sendDiscordNotification(`addTrack Error \n Error: ${e} `)
                alert('伺服器忙線中，請稍後再試')
              })
              .finally(() => {
                setIsCardLoading(false)
                setWarning({ ...warning, isLoading: false })
                clearTimeout(loadingTimer)
              })
          } else {
            axios
              .post(baseUrl + createPatient, requestBody, {
                headers: {
                  'Content-Type': 'application/json; charset=UTF-8',
                  Accept: '*/*',
                  session: currentUser.session,
                },
              })
              .then((res) => {
                if (res.data) {
                  console.log('createPatient', res.data)
                  // setbloodPressureList(res.data.data)
                  setQrcode({ show: true, msg: '資料建立成功！' })
                  setCaseModalShow(false)
                  extendCookie()
                }
              })
              .catch((e) => {
                console.log('createPatient error', e)
                sendDiscordNotification(`createPatient Error \n Error: ${e} `)
                alert('伺服器忙線中，請稍後再試')
              })
              .finally(() => {
                setIsCardLoading(false)
                setWarning({ ...warning, isLoading: false })
                clearTimeout(loadingTimer)
              })
          }
        }
      },
      onCancel: () => {},
    })
    // console.log('quitBetelNutTypeArray', quitBetelNutTypeArray)
  }

  const exerciseHabitTypeArr = ['1.無', '2.偶爾', '3.經常(每週累計達 150 分鐘)']
  const smokeHabitTypeArr = [
    '1.無',
    '2.偶爾',
    '3.平均一天約吸 10 支菸以下',
    '4.平均一天約吸 10 支菸（含）以上',
  ]
  const betelNutHabitTypeArr = ['1.無', '2.偶爾', '3.經常']
  const chronicRiskTypeArr = ['高風險', '中風險', '低風險', '不適用']
  const RecordSchema = Yup.object().shape({
    record_time: Yup.date().required('收案日期為必填項目'),
    next_appointment_time: Yup.date()
      .required('建議回診日期為必填項目')
      .when('record_time', (record_time, schema) => {
        return schema.min(
          dayjs(record_time).add(1, 'day').toDate(),
          '建議回診日期必須晚於收案日期'
        )
      }),
    track_type: Yup.string().required('追蹤方式為必填項目'),
    id_number: Yup.string()
      .required('身份證為必填項目')
      .matches(/^[A-Z]{1}\d{9}$/, '身分證必須符合格式'),
    phone: Yup.string()
      .required('電話為必填項目')
      .matches(/^\d{10}$/, '電話必須符合格式'),
  })
  return (
    <>
      <div className="metabolicPlanFormConfirm overflow-x-hidden mx-4 mx-lg-5 ">
        <h4 className="text-center text-danger mb-3">
          ※請再次確保表單內容資料正確，送出後無法修改！※
        </h4>
        <Row className="text-center">
          <Col>
            病人姓名：<span className="h5">{name}</span>
          </Col>
          <Col>
            性別：
            <span className="h5">{Number(gender) === 1 ? '男' : '女'}</span>
          </Col>
          <Col sm={6}>
            出生日期：
            <span className="h5">
              {birthday}({age}歲)
            </span>
          </Col>
        </Row>
        <Row xs={2} className="mt-2">
          <Col className="border border-light-subtle border-1 border-end-0 p-3">
            <h5 className="mb-3">基本資料與生活習慣</h5>
            <p>身高：{info.height} 公分</p>
            <p>體重：{info.weight} 公斤</p>
            <p>運動：{exerciseHabitTypeArr[info.exercise_habit_type - 1]}</p>
            <p>抽菸：{smokeHabitTypeArr[info.smoke_habit_type - 1]}</p>
            <p>嚼檳榔：{betelNutHabitTypeArr[info.betel_nut_habit_type - 1]}</p>
          </Col>
          <Col className="border border-light-subtle border-1 p-3">
            <h5 className="mb-3">
              得到慢性病的風險 <span className="h6">（僅需收案時評估）</span>
            </h5>
            <p>冠心病：{chronicRiskTypeArr[chronic_risk.cad_risk_type - 1]}</p>
            <p>
              糖尿病：{chronicRiskTypeArr[chronic_risk.diabetes_risk_type - 1]}
            </p>
            <p>
              高血壓：
              {chronicRiskTypeArr[chronic_risk.hypertension_risk_type - 1]}
            </p>
            <p>
              腦中風：{chronicRiskTypeArr[chronic_risk.stroke_risk_type - 1]}
            </p>
            <p>
              心血管不良事件：
              {chronicRiskTypeArr[chronic_risk.cvd_risk_type - 1]}
            </p>
          </Col>
        </Row>
        <div className="metabolicIndicators border border-light-subtle border-1 border-end border-start px-3 pb-3">
          <h5 className="my-3">代謝症候群五大指標及身體質量指數（參考值）</h5>
          <p>
            腰圍：{indicator.waistline} 公分
            <span className="text-black-50">
              （男性 ≧ 90 公分，女性 ≧ 80 公分）
            </span>
          </p>
          <p>
            飯前血糖值：{indicator.blood_sugar_before_meal} mg/dL
            <span className="text-black-50">（≧ 100 mg/dL）</span>
          </p>
          <p>
            血壓：收縮壓 {indicator.sbp} mmHg、舒張壓 {indicator.dbp} mmHg
            <span className="text-black-50">
              （收縮壓 ≧ 130 mmHg，舒張壓 ≧ 85 mmHg）
            </span>
          </p>
          <p>
            三酸甘油脂值：{indicator.tg} mg/dL
            <span className="text-black-50">（≧ 150 mg/dL）</span>
          </p>
          <p>
            高密度脂蛋白膽固醇值：{indicator.hdl} mg/dL
            <span className="text-black-50">
              （男性 ＜ 40 mg/dL，女性 ＜ 50 mg/dL）
            </span>
          </p>
          <p>
            身體質量指數 BMI：{indicator.bmi} kg/m<sup>2</sup>
            <span className="text-black-50">（≧　27）</span>
          </p>
        </div>
        <div className="diseaseManagementGuidelines border border-secondary border-3 px-3 pb-3">
          <h5 className="m-2 mt-4">
            疾病管理指引
            <span className="h6">
              （醫師對病人說明治療用藥外，亦須改善危險因子的重要性）
            </span>
          </h5>
          <div className="form-section">
            <div
              role="group"
              aria-labelledby="quitSmoking-radio-group"
              className="mt-2"
            >
              <label>
                <input
                  type="checkbox"
                  name="track_form.guidelines.quit_smoking.check"
                  value="1"
                  defaultChecked={guidelines.quit_smoking.check === 1}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                />
                戒菸（吸菸 ≧ 10 支/日或尼古丁成癮度 ≧ 4
                分，可提供或轉介戒菸服務）
              </label>
              <div className="ms-3">
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.quit_smoking.quit_smoking_type"
                    value="0"
                    defaultChecked={
                      guidelines.quit_smoking.quit_smoking_type === '0'
                    }
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  戒菸指導（無意願接受戒菸服務或 ＜ 10 支/日或尼古丁成癮度 ＜ 4
                  分）
                </label>
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.quit_smoking.quit_smoking_type"
                    value="1"
                    defaultChecked={
                      guidelines.quit_smoking.quit_smoking_type === '1'
                    }
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  戒菸服務（≧ 10 支/日或尼古丁成癮度 ≧ 4 分）：
                </label>
                <div className="ms-3">
                  <label>
                    <input
                      type="radio"
                      name="track_form.guidelines.quit_smoking.service_type"
                      value="0"
                      defaultChecked={
                        guidelines.quit_smoking.service_type === '0'
                      }
                      onChange={() => {}}
                      onClick={(e) => e.preventDefault()}
                    />
                    提供戒菸服務
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="track_form.guidelines.quit_smoking.service_type"
                      value="1"
                      defaultChecked={
                        guidelines.quit_smoking.service_type === '1'
                      }
                      onChange={() => {}}
                      onClick={(e) => e.preventDefault()}
                    />
                    同意轉介戒菸服務
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="form-section">
            <div
              role="group"
              aria-labelledby="track_form.guidelines.quit_betel_nut.check"
              className="mt-2"
            >
              <label>
                <input
                  type="checkbox"
                  name="track_form.guidelines.quit_betel_nut.check"
                  value="1"
                  defaultChecked={guidelines.quit_betel_nut.check === 1}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                />
                戒檳（6 個月內有嚼檳榔）
              </label>
              <div className="ms-3">
                <div>
                  <label>
                    <input
                      type="checkbox"
                      name="track_form.guidelines.quit_betel_nut.quit_betel_nut_type"
                      value="0"
                      defaultChecked={guidelines.quit_betel_nut.quit_betel_nut_type.includes(
                        '0'
                      )}
                      onChange={() => {}}
                      onClick={(e) => e.preventDefault()}
                    />
                    戒檳目標：
                  </label>
                  {guidelines.quit_betel_nut.target_date
                    ? dayjs(guidelines.quit_betel_nut.target_date).format(
                        'YYYY-MM-DD'
                      )
                    : ''}

                  <span> 前戒除檳榔</span>
                </div>
                <div>
                  <label>
                    <input
                      type="checkbox"
                      name="track_form.guidelines.quit_betel_nut.quit_betel_nut_type"
                      value="1"
                      defaultChecked={guidelines.quit_betel_nut.quit_betel_nut_type.includes(
                        '1'
                      )}
                      onChange={() => {}}
                      onClick={(e) => e.preventDefault()}
                    />
                    安排口腔黏膜檢查：
                  </label>
                  {guidelines.quit_betel_nut.oral_mucosa_check_date
                    ? dayjs(
                        guidelines.quit_betel_nut.oral_mucosa_check_date
                      ).format('YYYY-MM')
                    : ''}
                </div>
              </div>
            </div>
          </div>
          <div className="form-section">
            <div
              role="group"
              aria-labelledby="track_form.guidelines.food"
              className="mt-2"
            >
              <label>
                <input
                  type="checkbox"
                  name="track_form.guidelines.food.check"
                  value="1"
                  defaultChecked={guidelines.food.check === 1}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                />
                每日建議攝取熱量（大卡/天）：
              </label>
              <div className="ms-3">
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.food.caloric_intake_type"
                    value="1"
                    defaultChecked={guidelines.food.caloric_intake_type === '1'}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  1200
                </label>
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.food.caloric_intake_type"
                    value="2"
                    defaultChecked={guidelines.food.caloric_intake_type === '2'}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  1500
                </label>
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.food.caloric_intake_type"
                    value="3"
                    defaultChecked={guidelines.food.caloric_intake_type === '3'}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  1800
                </label>
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.food.caloric_intake_type"
                    value="4"
                    defaultChecked={guidelines.food.caloric_intake_type === '4'}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  2000
                </label>
                <label>
                  <input
                    type="radio"
                    name="track_form.guidelines.food.caloric_intake_type"
                    value="5"
                    defaultChecked={guidelines.food.caloric_intake_type === '5'}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  其他：
                  {guidelines.food.caloric ? guidelines.food.caloric : ''}
                </label>
              </div>
            </div>
          </div>
          <div className="form-section">
            <div
              role="group"
              aria-labelledby="track_form.guidelines.food.reduce_food_type"
              className="mt-2"
            >
              <div className="ms-3">
                盡量減少
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.food.reduce_food_type"
                    value="friedFood"
                    defaultChecked={guidelines.food.reduce_food_type.includes(
                      'friedFood'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  油炸物
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.food.reduce_food_type"
                    value="sweets"
                    defaultChecked={guidelines.food.reduce_food_type.includes(
                      'sweets'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  甜食
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.food.reduce_food_type"
                    value="salt"
                    defaultChecked={guidelines.food.reduce_food_type.includes(
                      'salt'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  鹽
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.food.reduce_food_type"
                    value="sugaryDrinks"
                    defaultChecked={guidelines.food.reduce_food_type.includes(
                      'sugaryDrinks'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  含糖飲料
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.food.reduce_food_type"
                    value="others"
                    defaultChecked={guidelines.food.reduce_food_type.includes(
                      'others'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  其他：
                  {guidelines.food.other_food_type
                    ? guidelines.food.other_food_type
                    : ''}
                </label>
              </div>
            </div>
          </div>
          <div className="form-section">
            <div
              role="group"
              aria-labelledby="track_form.guidelines.exercise"
              className="mt-2"
            >
              <label>
                <input
                  type="checkbox"
                  name="track_form.guidelines.exercise.check"
                  value="1"
                  defaultChecked={guidelines.exercise.check === 1}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                />
                運動建議：
              </label>
              <div className="ms-3">
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.exercise.exercise_type"
                    value="0"
                    defaultChecked={guidelines.exercise.exercise_type.includes(
                      '0'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  提供運動指導:每週累積 150
                  分鐘中等費力運動（活動時仍可交談，但無法唱歌）
                </label>
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.exercise.exercise_type"
                    value="1"
                    defaultChecked={guidelines.exercise.exercise_type.includes(
                      '1'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  提供社區運動資源：
                  {guidelines.exercise.resource
                    ? guidelines.exercise.resource
                    : ''}
                </label>
              </div>
            </div>
          </div>
          <div className="form-section">
            <div
              role="group"
              aria-labelledby="track_form.guidelines.target_indicator"
              className="mt-2"
            >
              <label>
                <input
                  type="checkbox"
                  name="track_form.guidelines.target_indicator.check"
                  value="1"
                  defaultChecked={guidelines.target_indicator.check === 1}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                />
                想達成的腰圍及體重：
              </label>
              <div className="ms-3">
                <label>
                  腰圍{' '}
                  {guidelines.target_indicator.target_waistline
                    ? guidelines.target_indicator.target_waistline
                    : ''}{' '}
                  公分、體重{' '}
                  {guidelines.target_indicator.target_waistline
                    ? guidelines.target_indicator.target_weight
                    : ''}{' '}
                  公斤
                </label>
              </div>
            </div>
          </div>
          <div className="form-section">
            <div className="mt-2">
              <label>
                <input
                  type="checkbox"
                  name="track_form.guidelines.blood_pressure_check"
                  value="1"
                  defaultChecked={guidelines.blood_pressure_check.check === 1}
                  onChange={() => {}}
                  onClick={(e) => e.preventDefault()}
                />
                量血壓：指導 722 量測
              </label>
            </div>
          </div>
          <div className="mt-2">
            <label>
              <input
                type="checkbox"
                name="track_form.guidelines.other.check"
                value="1"
                defaultChecked={guidelines.other.check === 1}
                onChange={() => {}}
                onClick={(e) => e.preventDefault()}
              />
              其他叮嚀：
            </label>
            <div className="ms-3">
              <div className="form-section">
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.other.other_type"
                    value="blood_sugar"
                    defaultChecked={guidelines.other.other_type.includes(
                      'blood_sugar'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  飯前血糖個人目標值：{' '}
                </label>
                {guidelines.other.blood_sugar_before_meal_target
                  ? guidelines.other.blood_sugar_before_meal_target +
                    ' mg/dL（%）'
                  : ''}
              </div>
              <div className="form-section">
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.other.other_type"
                    value="HbA1c"
                    defaultChecked={guidelines.other.other_type.includes(
                      'HbA1c'
                    )}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  醣化血紅素個人目標值：
                </label>
                {guidelines.other.HbA1c_before_meal_target
                  ? guidelines.other.HbA1c_before_meal_target + ' mg/dL（%）'
                  : ''}
              </div>
              <div className="form-section">
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.other.other_type"
                    value="tg"
                    defaultChecked={guidelines.other.other_type.includes('tg')}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  三酸甘油脂個人目標值：
                </label>
                {guidelines.other.tg_target
                  ? guidelines.other.tg_target + ' mg/dL'
                  : ''}
              </div>
              <div className="form-section">
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.other.other_type"
                    value="hdl"
                    defaultChecked={guidelines.other.other_type.includes('hdl')}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  高密度脂蛋白膽固醇個人目標值：
                </label>
                {guidelines.other.hdl_target
                  ? guidelines.other.hdl_target + ' mg/dL'
                  : ''}
              </div>
              <div className="form-section">
                <label>
                  <input
                    type="checkbox"
                    name="track_form.guidelines.other.other_type"
                    value="ldl"
                    defaultChecked={guidelines.other.other_type.includes('ldl')}
                    onChange={() => {}}
                    onClick={(e) => e.preventDefault()}
                  />
                  低密度脂蛋白膽固醇個人目標值：
                </label>
                {guidelines.other.ldl_target
                  ? guidelines.other.ldl_target + ' mg/dL'
                  : ''}
              </div>
            </div>
          </div>
        </div>
        <Formik
          initialValues={initialValues}
          // TODO yup schema
          validationSchema={RecordSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            // Handle form submission, e.g., send data to an API

            handleSubmit(values)
            // Example: Setting a status message after submission
            // setStatus({ submitted: true });

            // Reset the form after successful submission
            // resetForm()

            // Set submitting to false after form is handled
            setSubmitting(false)
          }}
        >
          {({
            errors,
            touched,
            isSubmitting,
            status,
            setFieldValue,
            values,
          }) => (
            <Form>
              <Row xs={2} className="align-items-center">
                <div className="form-section">
                  <label htmlFor="date">收案日期：</label>
                  <DatePicker
                    className="w-100"
                    name="record_time"
                    dateFormat="yyyy/MM/dd"
                    selected={values.record_time}
                    onChange={(date) =>
                      setFieldValue(
                        'record_time',
                        dayjs(date).format('YYYY-MM-DD')
                      )
                    }
                  />
                  {renderErrorMessage(errors, touched, 'record_time')}
                </div>
                <div className="form-section">
                  <label htmlFor="currentUser">收案單位：</label>
                  {currentUser?.name}
                  {renderErrorMessage(errors, touched, 'currentUser')}
                </div>
              </Row>

              <Row xs={2} className="align-items-center">
                <div className="form-section">
                  {/* TODO: 不能比原日期小  */}
                  <label htmlFor="next_appointment_time">建議回診日期：</label>
                  <DatePicker
                    className="w-100"
                    name="next_appointment_time"
                    dateFormat="yyyy/MM/dd"
                    selected={values.next_appointment_time}
                    onChange={(date) =>
                      setFieldValue(
                        'next_appointment_time',
                        dayjs(date).format('YYYY-MM-DD')
                      )
                    }
                  />
                  {renderErrorMessage(errors, touched, 'next_appointment_time')}
                </div>
                <div className="form-section">
                  <label>追蹤方式：</label>
                  <Field
                    className={getClassName(errors, touched, 'track_type')}
                    as="select"
                    name="track_type"
                  >
                    <option value="">選擇</option>
                    <option value="0">初診</option>
                    <option value="1">電話追蹤</option>
                    <option value="2">訪視</option>
                    <option value="3">回診</option>
                  </Field>
                  {renderErrorMessage(errors, touched, 'track_type')}
                </div>
              </Row>

              <Row xs={2} className="align-items-center">
                <div className="form-section">
                  <label htmlFor="id_number">個案身分證：</label>
                  {isTrackDataLoaded ? (
                    <>
                      <span className="h5">{lastTrackFormData.id_number}</span>
                    </>
                  ) : (
                    <Field
                      name="id_number"
                      type="text"
                      className={getClassName(errors, touched, 'id_number')}
                    />
                  )}

                  {renderErrorMessage(errors, touched, 'id_number')}
                </div>
                <div className="form-section">
                  <label htmlFor="phone">個案聯絡電話：</label>
                  {isTrackDataLoaded ? (
                    <>
                      <span className="h5">{lastTrackFormData.phone}</span>
                    </>
                  ) : (
                    <Field
                      name="phone"
                      type="text"
                      className={getClassName(errors, touched, 'phone')}
                    />
                  )}

                  {renderErrorMessage(errors, touched, 'phone')}
                </div>
              </Row>
              <h4 className="text-center text-danger my-3">
                ※請再次確保表單內容資料正確，送出後無法修改！※
              </h4>
              <div className="d-flex gap-3">
                <Button
                  variant="outline-primary"
                  className="my-3 w-25"
                  onClick={() => {
                    setFormConfirmShow(false)
                  }}
                >
                  回到上一步
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="my-3 w-75"
                >
                  確認送出
                  <Spinner
                    as="span"
                    animation={isCardLoading ? 'border' : ''}
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  )
}

export default MetabolicPlanFormConfirm
